import { IPublicClientApplication } from '@azure/msal-browser';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { ENV } from 'config';

import { ApiError, BEErrorHandler, ErrorSeverity, ErrorTypes } from './error';
import { setupAxiosInterceptors } from './interceptors';

export const API_TIMEOUT = 60000;

const backEndURL = `${ ENV.API_URL }`;

const axiosInstance = axios.create({
    baseURL: `${ backEndURL }/api/`,
    timeout: API_TIMEOUT
});

const initApi = () => {
    let interceptorsInitialized = false;

    return {
        initializeInterceptors: (msalInstance: IPublicClientApplication) => {
            if (interceptorsInitialized) {
                return;
            }

            setupAxiosInterceptors(axiosInstance, { msalInstance });

            interceptorsInitialized = true;
        },
        delete: async (url: string, ErrorHandler = BEErrorHandler): Promise<true | ApiError> => {
            try {
                const response = await axiosInstance.delete(url);

                if (!response || response.status === 200) {
                    return true;
                }
            } catch (error) {
                const errorHandler = new ErrorHandler(error as AxiosError);

                return errorHandler.handle();
            }

            return new ApiError(ErrorSeverity.error, 'Could not process request. Please contact administrator', ErrorTypes.General);
        },
        get: async <T>(fetchUrl: string, ErrorHandler = BEErrorHandler, config: AxiosRequestConfig = {}): Promise<T | ApiError> => {
            try {
                const response = await axiosInstance.get<T>(fetchUrl, config);

                if (!response || response.status === 200) {
                    return response.data;
                }
            } catch (error) {
                const errorHandler = new ErrorHandler(error as AxiosError);

                return errorHandler.handle();
            }

            return new ApiError(ErrorSeverity.error, 'Could not get data. Please contact administrator', ErrorTypes.General);
        },
        post: async <DataType, ResponseType>(postUrl: string, data: DataType, ErrorHandler = BEErrorHandler, config: AxiosRequestConfig = {}): Promise<ResponseType | ApiError> => {
            try {
                const response = await axiosInstance.post<ResponseType>(postUrl, data, config);

                return response.data;
            } catch (error) {
                const errorHandler = new ErrorHandler(error as AxiosError);

                return errorHandler.handle();
            }
        },
        put: async <DataType, ResponseType>(postUrl: string, data: DataType, ErrorHandler = BEErrorHandler, config: AxiosRequestConfig = {}): Promise<ResponseType | ApiError> => {
            try {
                const response = await axiosInstance.put<ResponseType>(postUrl, data, config);

                return response.data;
            } catch (error) {
                const errorHandler = new ErrorHandler(error as AxiosError);

                return errorHandler.handle();
            }
        }
    };
};

// Backwards compatibility
axios.defaults.baseURL = `${ ENV.API_URL }/api/`;

export const api = initApi();
export default axios;
// End of backwards compatibility

export { axiosInstance };
